import React, { useEffect, useRef, useState } from "react";
import { CSSTransition } from "react-transition-group";
import playerStyles from "../../_styles/FeedPlayer.module.scss";
import controlStyles from "../../_styles/FeedControls.module.scss";
import { ISectionItemProps } from "../../_types/interfaces";
import useInViewport from "../../../../hooks/useInViewPort";
import ReactPlayer from "react-player";
import MobileBottomInfoBlock from "../MobileComponents/MobileBottomInfoBlock";
import MobileProgressController from "../MobileComponents/MobileProgressController";
import useInterfaceContext from "../../../../components/providers/interface";
import { useDispatch } from "react-redux";
import {
  setCurrentItem,
  setCurrentVideoId,
  setIsAds,
  setLastVisitedId,
} from "../../../../store/slices/currentItem.slice";
import { ROUTES } from "../../../../constants/routes";
import { useNavigate } from "react-router-dom";
import { useGetCurrentVideoQuery } from "../../../../store/api/filmAPI";
import { useTranslation } from "react-i18next";
import { languageHandler } from "../../../../utils/helpers";
import { AdSkip } from "../../../../components/shared/AdsSkip/AdsSkip";
import AdsBottomMenu from "../playerController/AdsBottomMenu";
import MutedButtonOn from "../../../../components/icons/MutedButtonOn";
import MutedButtonOff from "../../../../components/icons/MutedButtonOff";
import { useAppSelector } from "../../../../store/store";
import { setMutedState } from "../../../../store/slices/filters.slice";

const applyYoutubeMutedState = (youtubePlayer: any, shouldMute: boolean) => {
  if (!youtubePlayer) return;

  if (shouldMute) {
    youtubePlayer?.mute?.();
    return;
  }

  youtubePlayer?.unMute?.();
  youtubePlayer?.setVolume?.(100);
};

const MobilePlayer = ({
  item,
  horizontal,
  currentId,
  filterErrorItem,
  skipAdsHandler,
  onTrailerWatched,
}: ISectionItemProps) => {
  const [url, setUrl] = useState("");
  const [player, setPlayer] = useState(null);
  const [time, setTime] = useState<number | number[]>(0);
  const [maxValue, setMaxValue] = useState<number | number[]>(0);
  const [ready, setReady] = useState(false);
  const [playing, setPlaying] = useState(true);
  const [muteTouched, setMuteTouched] = useState(false);
  const errorHandledRef = useRef(false);
  const dispatch = useDispatch();
  const muted = useAppSelector((state) => state.filters.mutedState);
  const playerMuted = muteTouched ? muted : true;
  const navigate = useNavigate();

  const { i18n, t } = useTranslation();
  const { isMobile } = useInterfaceContext();
  const { inViewport, targetRef } = useInViewport();
  const [showMutePrompt, setShowMutePrompt] = useState(false);

  useEffect(() => {
    if (!inViewport || !playerMuted) {
      setShowMutePrompt(false);
      return;
    }

    const timer = setTimeout(() => setShowMutePrompt(true), 150);
    return () => clearTimeout(timer);
  }, [inViewport, playerMuted]);

  const { data } = useGetCurrentVideoQuery(
    {
      queries: { language: languageHandler(i18n.language) },
      id: item.id,
    },
    {
      skip: item.commercial_url ? true : !inViewport,
    },
  );

  const clickHandler = () => {
    if (ready) {
      navigate(`${ROUTES.DETAILS}/${item.id}`);
      dispatch(setLastVisitedId(item.id as number));
    }
  };

  const togglePlayback = () => {
    const youtubePlayer = player as any;

    if (playing) {
      youtubePlayer?.pauseVideo?.();
      setPlaying(false);
      return;
    }

    youtubePlayer?.playVideo?.();
    setPlaying(true);
  };

  const toggleMute = () => {
    const nextMuted = !playerMuted;
    setMuteTouched(true);
    dispatch(setMutedState(nextMuted));
    setPlaying(true);

    const youtubePlayer = player as any;
    applyYoutubeMutedState(youtubePlayer, nextMuted);
    youtubePlayer?.playVideo?.();
  };

  useEffect(() => {
    applyYoutubeMutedState(player, playerMuted);
  }, [player, playerMuted]);

  useEffect(() => {
    if (item.commercial_url) {
      setUrl(item.commercial_url);
      return;
    }

    if (!data?.results) return;

    errorHandledRef.current = false;
    const trailer = data.results.find((item: any) => item.type === "Trailer") || data.results[0];
    setUrl(trailer ? trailer?.key : "");

    if (inViewport && !trailer) {
      errorHandledRef.current = true;
      filterErrorItem(item.id);
    }
  }, [data, filterErrorItem, inViewport, item]);

  const handlePlayerError = () => {
    if (errorHandledRef.current) return;

    errorHandledRef.current = true;
    filterErrorItem(item.id);
  };

  useEffect(() => {
    if (inViewport) {
      dispatch(setCurrentVideoId(item.id));
      dispatch(setCurrentItem(item));
      dispatch(setIsAds(!!item.commercial_url));
      return;
    }
    const timer = window.setTimeout(() => {
      setReady(false);
      setPlaying(true);
      setMuteTouched(false);
    }, 400);

    return () => window.clearTimeout(timer);
  }, [inViewport, item, dispatch]);
  const dirData = i18n.language === "ar" ? "rtl" : "ltr";
  const isAd = !!item?.commercial_url;

  const playerBlock = (
    <div className={`${playerStyles.playerBox} ${horizontal ? playerStyles.playerBoxHorizontal : ""}`}>
      {!horizontal && <div className={playerStyles.playerGradientTop} />}
      <div dir={dirData} className={`${playerStyles.player} ${horizontal ? playerStyles.playerNoRadius : ""}`}>
        {isAd && inViewport && (
          <AdSkip horizontal={horizontal} onSkip={() => skipAdsHandler("down", item.id)} />
        )}
        {url ? (
          <div className={horizontal ? playerStyles.playerLandscapeCrop : playerStyles.playerPortraitFrame}>
            <div className={`${playerStyles.playerCropInner} ${!horizontal ? playerStyles.playerPortraitCrop : ""}`}>
              <ReactPlayer
                className={playerStyles.mobilePlayerFrame}
                controls={false}
                width={"100%"}
                height={"100%"}
                playsinline={true}
                progressInterval={500}
                onProgress={(value: any) => setTime(Math.round(value.playedSeconds))}
                playing={playing}
                onPlay={() => setPlaying(true)}
                loop={true}
                style={{ background: "#000000" }}
                url={`https://www.youtube.com/watch?v=${url}`}
                muted={playerMuted}
                onPause={() => setPlaying(false)}
                onError={handlePlayerError}
                onReady={(e: any) => {
                  const youtubePlayer = e["player"];
                  setPlayer(youtubePlayer);
                  const duration = youtubePlayer.getDuration();
                  setMaxValue(duration);

                  onTrailerWatched(item.title, `https://www.youtube.com/watch?v=${url}`);

                  applyYoutubeMutedState(youtubePlayer, playerMuted);
                  youtubePlayer?.playVideo?.();
                  setPlaying(true);

                  setTimeout(() => {
                    setReady(true);
                  }, 200);
                }}
              />
            </div>
          </div>
        ) : (
          <div style={{ height: "350px", width: "100%", background: "#000000" }} />
        )}
        {horizontal && <div className={playerStyles.playerLandscapeBarTop} />}
        {horizontal && <div className={playerStyles.playerLandscapeBarBottom} />}
        {url && (
          <button
            type="button"
            className={playerStyles.mobileVideoToggle}
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              togglePlayback();
            }}
            aria-label={playing ? "Pause video" : "Play video"}
          />
        )}
        {horizontal && !isAd && (
          <div className={playerStyles.playerLandscapeProgressController}>
            <MobileProgressController
              maxValue={maxValue as number}
              time={time as number}
              player={player as any}
              setTime={setTime}
              isMobile={isMobile}
            />
          </div>
        )}
        {!isAd && (
          <>
            <CSSTransition
              in={showMutePrompt}
              timeout={400}
              classNames={{
                enter: controlStyles.fadeEnter,
                enterActive: controlStyles.fadeEnterActive,
                exit: controlStyles.fadeExit,
                exitActive: controlStyles.fadeExitActive,
              }}
              unmountOnExit
            >
              <button
                type="button"
                className={`${controlStyles.mobileMuteButton} ${controlStyles.muted}`}
                onClick={toggleMute}
                aria-label="Unmute"
              >
                <span key="muted" className={controlStyles.mobileMuteIcon}>
                  <MutedButtonOn/>
                </span>
                <span className={controlStyles.mobileMuteButtonText}>{t("turn_on")}</span>
              </button>
            </CSSTransition>

            <CSSTransition
              in={!playerMuted}
              timeout={250}
              classNames={{
                enter: controlStyles.simpleFadeEnter,
                enterActive: controlStyles.simpleFadeEnterActive,
                exit: controlStyles.simpleFadeExit,
                exitActive: controlStyles.simpleFadeExitActive,
              }}
              unmountOnExit
            >
              <button
                type="button"
                className={`${controlStyles.mobileMuteButton} ${controlStyles.unmuted} ${horizontal ? controlStyles.unmutedHorizontal : ""}`}
                onClick={toggleMute}
                aria-label="Mute"
              >
                <span key="unmuted" className={controlStyles.mobileMuteIcon}>
                  <MutedButtonOff/>
                </span>
              </button>
            </CSSTransition>
          </>
        )}
      </div>
      {!horizontal && <div className={playerStyles.playerGradientBottom} />}
    </div>
  );

  return (
    <div ref={targetRef} className={`${playerStyles.itemWrapper} ${horizontal ? playerStyles.itemWrapperHorizontal : ""}`}>
      {inViewport &&
        (isAd && !horizontal ? (
          <div className={playerStyles.adGroup}>
            {playerBlock}
            <AdsBottomMenu />
          </div>
        ) : isAd && horizontal ? (
          <div className={playerStyles.adGroupHorizontal}>{playerBlock}</div>
        ) : (
          <>
            {playerBlock}
            {!isAd && !horizontal && (
              <div className={controlStyles.bottomControlWrapper}>
                <MobileBottomInfoBlock
                  item={item}
                  currentItemId={item.id}
                  voteCount={item.vote_count}
                  isMobile={isMobile}
                  clickHandler={clickHandler}
                />
                <div className={controlStyles.mobileProgressController}>
                  <MobileProgressController
                    maxValue={maxValue as number}
                    time={time as number}
                    player={player as any}
                    setTime={setTime}
                    isMobile={isMobile}
                  />
                </div>
              </div>
            )}
          </>
        ))}
    </div>
  );
};

export default MobilePlayer;
